// Administration department dashboard, laid out like the HR dashboard. Staff / labour headcount
// come from GetAdminDashboardSummary (CC-scoped server-side by RoleId/UserId) — see
// spGetAdminDashboardSummary.sql. Licence / insurance expiry and blacklist items are listed but
// have no data source yet, so they show "No data yet" instead of a count.
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Users, HardHat, FileBadge, ShieldCheck, UserX, Ban, ShieldX, Loader2 } from 'lucide-react';
import FrequentAccessCard from './FrequentAccessCard';
import { TILE_TONES } from './StatTile';
import { getAdminDashboardSummary } from '../../api/HRAPI/hrDashboardAPI';

// Items with no backend yet — shown as a list so the layout is ready once data exists
const NEEDS_ATTENTION_ROWS = [
    { key: 'LicenseExpiring', title: 'Licenses expiring', subtitle: 'Company / site licenses due for renewal', icon: FileBadge, tone: 'orange', dueLabel: 'Next 30 days' },
    { key: 'InsuranceExpiring', title: 'Insurance expiring', subtitle: 'Policies due for renewal', icon: ShieldCheck, tone: 'orange', dueLabel: 'Next 30 days' },
    { key: 'BlacklistedPeople', title: 'Blacklisted people', subtitle: 'Staff / labour marked as blacklisted', icon: UserX, tone: 'red', dueLabel: 'Current' },
    { key: 'BlacklistedVendors', title: 'Blacklisted vendors', subtitle: 'Vendors / service providers', icon: Ban, tone: 'red', dueLabel: 'Current' },
    { key: 'BlacklistedClients', title: 'Blacklisted clients', subtitle: 'Clients marked as blacklisted', icon: ShieldX, tone: 'red', dueLabel: 'Current' },
];

// Headline tile — same style as HR's Active Headcount tile
const HeadcountTile = ({ title, subtitle, icon: Icon, value, loading }) => (
    <div className="sm:col-span-2 bg-gradient-to-br from-[#0d1b5e] to-[#16247f] dark:from-[#0a1240] dark:to-[#111a4d] rounded-lg p-5 text-white shadow-md flex flex-col justify-between">
        <div className="flex items-start justify-between">
            <p className="text-sm font-medium text-blue-100">{title}</p>
            <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-white" />
            </div>
        </div>
        <div className="mt-3">
            <p className="text-4xl font-bold">
                {loading ? <Loader2 className="w-7 h-7 animate-spin text-white" /> : (value ?? 0)}
            </p>
            <p className="text-xs text-blue-200 mt-1">{subtitle}</p>
        </div>
    </div>
);

const AdminDashboard = ({ onNavigate, linkFrequency, trackMenuUsage }) => {
    const { roleData, userData } = useSelector((state) => state.auth);

    const userId = userData?.userId || userData?.UID || userData?.employeeId || '';
    const roleId = userData?.roleId || userData?.RID || 0;

    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // roleId/userId drive backend CC-scoping: for roles with ApplicableForCC='Yes',
        // counts are restricted to the cost centers assigned to this user in UserCostCenters.
        let cancelled = false;
        setLoading(true);
        getAdminDashboardSummary({ roleId, userId })
            .then((response) => { if (!cancelled) setSummary(response?.Data || null); })
            .catch(() => { if (!cancelled) setSummary(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [roleId, userId]);

    return (
        <div className="space-y-6">
            {/* Welcome Header */}
            <div className="bg-gradient-to-r from-[#0d1b5e] to-[#0a1240] dark:from-[#0a1240] dark:to-gray-900 rounded-lg p-6 text-white transition-colors">
                <h1 className="text-2xl font-bold mb-2">Welcome Back!</h1>
                <p className="text-orange-200 dark:text-orange-300">Employee: {userData?.firstName} | Role: {userData?.roleCode}</p>
            </div>

            {/* Headcount + needs-attention on one side, Quick access on the other */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                <div className="lg:col-span-2 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                        <HeadcountTile
                            title="Staff Headcount"
                            subtitle="Approved, currently employed"
                            icon={Users}
                            loading={loading}
                            value={summary?.StaffHeadcount}
                        />
                        <HeadcountTile
                            title="Labour Headcount"
                            subtitle="Approved, currently working"
                            icon={HardHat}
                            loading={loading}
                            value={summary?.LabourHeadcount}
                        />
                    </div>

                    {/* Needs attention — items only, no data source yet */}
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 p-4">
                        <div className="flex items-start justify-between mb-1">
                            <div>
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Needs attention</h3>
                                <p className="text-xs text-gray-400 dark:text-gray-500">Expiries and blacklists</p>
                            </div>
                        </div>

                        <div className="divide-y divide-gray-100 dark:divide-gray-700 mt-2">
                            {NEEDS_ATTENTION_ROWS.map((row) => {
                                const Icon = row.icon;
                                const t = TILE_TONES[row.tone] || TILE_TONES.blue;
                                return (
                                    <div key={row.key} className="w-full flex items-center gap-3 py-2.5 px-1 -mx-1">
                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${t.icon}`}>
                                            <Icon className={`w-4 h-4 ${t.text}`} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{row.title}</p>
                                            <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{row.subtitle}</p>
                                        </div>
                                        <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0 hidden sm:block">{row.dueLabel}</span>
                                        <span className="text-lg font-bold w-6 text-right flex-shrink-0 text-gray-300 dark:text-gray-600">–</span>
                                        <span className="text-xs font-medium flex-shrink-0 w-20 text-right text-gray-400 dark:text-gray-500">
                                            No data yet
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-1">
                    <FrequentAccessCard
                        roleData={roleData}
                        linkFrequency={linkFrequency}
                        trackMenuUsage={trackMenuUsage}
                        onNavigate={onNavigate}
                    />
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
